/**
 * Client wrapper for the high-risk-intake-scheduling Bot (authenticated Patient session).
 *
 * Provides team availability for the screening call and atomic booking of the screening
 * Appointment + ServiceRequest/EpisodeOfCare pairs on confirm. Used only on the high-risk
 * intake path.
 */
import { ContentType } from '@medplum/core';
import type { MedplumClient } from '@medplum/core';
import type { Identifier } from '@medplum/fhirtypes';

export const HIGH_RISK_INTAKE_SCHEDULING_BOT_IDENTIFIER = {
  system: 'https://unify.health/bots',
  value: 'high-risk-intake-scheduling',
} as const;

function getBotTarget(): string | Identifier {
  const id = import.meta.env.VITE_MEDPLUM_HIGH_RISK_INTAKE_SCHEDULING_BOT_ID?.trim();
  if (id) {
    return id;
  }
  const system =
    import.meta.env.VITE_MEDPLUM_HIGH_RISK_INTAKE_SCHEDULING_BOT_IDENTIFIER_SYSTEM?.trim() ??
    HIGH_RISK_INTAKE_SCHEDULING_BOT_IDENTIFIER.system;
  const value =
    import.meta.env.VITE_MEDPLUM_HIGH_RISK_INTAKE_SCHEDULING_BOT_IDENTIFIER_VALUE?.trim() ??
    HIGH_RISK_INTAKE_SCHEDULING_BOT_IDENTIFIER.value;
  return { system, value };
}

export interface HighRiskAvailabilitySlot {
  start: string;
  end: string;
  /** Practitioner assigned to this time (not shown to the client; needed for booking). */
  practitionerId: string;
}

export interface HighRiskAvailabilityResult {
  date: string;
  timeZone: string;
  availableWeekdays: string[];
  slots: HighRiskAvailabilitySlot[];
}

export interface HighRiskConfirmBookingResult {
  appointmentId: string;
  serviceRequestIds: string[];
  episodeOfCareIds: string[];
  alreadyBooked: boolean;
}

/** Open screening-call times across the high-risk screening team for one day. */
export async function getHighRiskAvailability(
  medplum: MedplumClient,
  input: { date: string; timeZone?: string },
): Promise<HighRiskAvailabilityResult> {
  return medplum.executeBot(
    getBotTarget(),
    { action: 'get-availability', date: input.date, timeZone: input.timeZone },
    ContentType.JSON,
  ) as Promise<HighRiskAvailabilityResult>;
}

/** Atomically book the screening call + create screening ServiceRequest/EpisodeOfCare pairs. */
export async function confirmHighRiskBooking(
  medplum: MedplumClient,
  input: {
    start: string;
    end: string;
    practitionerId: string;
    therapyTypeCodes: string[];
    payType?: 'insurance' | 'cash' | null;
    organizationId?: string;
  },
): Promise<HighRiskConfirmBookingResult> {
  return medplum.executeBot(
    getBotTarget(),
    {
      action: 'confirm-booking',
      start: input.start,
      end: input.end,
      practitionerId: input.practitionerId,
      therapyTypeCodes: input.therapyTypeCodes,
      payType: input.payType ?? undefined,
      organizationId: input.organizationId,
    },
    ContentType.JSON,
  ) as Promise<HighRiskConfirmBookingResult>;
}

export interface BookScreeningCallResult {
  appointmentId: string;
  alreadyBooked: boolean;
}

/**
 * Staff path (Service Request Tracker): book a screening call on behalf of an existing client.
 * Creates only the screening-call Appointment — no new ServiceRequest/EpisodeOfCare. Requires a
 * signed-in Practitioner session and an explicit `patientId`.
 */
export async function bookHighRiskScreeningCall(
  medplum: MedplumClient,
  input: {
    patientId: string;
    start: string;
    end: string;
    practitionerId: string;
  },
): Promise<BookScreeningCallResult> {
  return medplum.executeBot(
    getBotTarget(),
    {
      action: 'book-screening-call',
      patientId: input.patientId,
      start: input.start,
      end: input.end,
      practitionerId: input.practitionerId,
    },
    ContentType.JSON,
  ) as Promise<BookScreeningCallResult>;
}

/** True when the booking failed because the chosen time was taken (HTTP 409 / conflict). */
export function isHighRiskBookingConflict(err: unknown): boolean {
  const s = String(err instanceof Error ? err.message : err).toLowerCase();
  return s.includes('409') || s.includes('conflict') || s.includes('just booked');
}
